import { memo } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { cn } from '@shared/lib';
import { getLanguageInfo, useLanguage, useLanguageSwitch } from '@infra/i18n';
import { translate } from '../a11y-labels';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/ui';
import { brandColors, brandRgba } from '@shared/constants';
import { useLanguagePickerVisible } from './useLanguagePickerVisible';

/**
 * Header language switcher (V2). Data-driven: it lists every language returned
 * by `availableLanguages` (sourced from LANGUAGE_INFO), so adding a 3rd/4th
 * language needs no change here — just register it in i18n.languages.ts.
 */
export const LanguageToggle = memo(function LanguageToggle() {
  const { language, availableLanguages } = useLanguage();
  const switchLang = useLanguageSwitch();
  const visible = useLanguagePickerVisible();

  if (!visible) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={translate('common:a11y.changeLanguage', 'Change language')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            height: 40,
            padding: '0 14px',
            borderRadius: 999,
            background: brandRgba('surfaceCard', 0.62),
            border: `1px solid ${brandColors.border}`,
            color: brandColors.textSecondary,
            fontWeight: 700,
            fontSize: 13,
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = brandColors.canvasAlt;
            (e.currentTarget as HTMLButtonElement).style.color = brandColors.brandPrimary;
            (e.currentTarget as HTMLButtonElement).style.borderColor = brandColors.borderStrongAlt;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = brandRgba('surfaceCard', 0.62);
            (e.currentTarget as HTMLButtonElement).style.color = brandColors.textSecondary;
            (e.currentTarget as HTMLButtonElement).style.borderColor = brandColors.border;
          }}
        >
          <Globe style={{ width: 15, height: 15 }} />
          <span style={{ fontWeight: 800 }}>{getLanguageInfo(language).badge}</span>
          <ChevronDown style={{ width: 13, height: 13, color: brandColors.textMuted }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={`min-w-[180px] rounded-2xl border border-[var(--brand-border)] shadow-xl p-0 overflow-hidden`}
      >
        {availableLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => {
              if (lang.code !== language)
                void switchLang(lang.code).catch((e) =>
                  console.error('Language switch failed:', e)
                );
            }}
            className={cn(
              'cursor-pointer gap-3 rounded-none text-[13.5px] px-4 py-3',
              lang.code === language
                ? `bg-[rgba(var(--brand-rgb-brand-primary),0.06)] text-[var(--brand-brand-primary)] font-semibold`
                : `text-[var(--brand-text-secondary)]`
            )}
          >
            <span
              style={{
                width: 32,
                height: 24,
                borderRadius: 6,
                display: 'grid',
                placeItems: 'center',
                fontSize: 10.5,
                fontWeight: 800,
                textTransform: 'uppercase',
                flexShrink: 0,
                background:
                  lang.code === language
                    ? `linear-gradient(135deg,${brandColors.brandPrimary} 0%,${brandColors.brandPrimaryAlt} 100%)`
                    : brandColors.canvas,
                color: lang.code === language ? brandColors.onDark : brandColors.textDeep,
              }}
            >
              {lang.badge}
            </span>
            <span className="flex-1">{lang.nativeName}</span>
            {lang.code === language && <Check className={`h-4 w-4 text-[var(--brand-brand-primary-alt)]`} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
